/**
 * analytics.ts — Type-safe Google Tag Manager (GTM) dataLayer helpers.
 *
 * All functions are no-ops when GTM is not loaded (e.g. in dev, tests,
 * or when REACT_APP_GTM_ID is unset). This keeps analytics code safe
 * to call anywhere without conditional checks at the call site.
 */

/* ─── Extend the Window interface for dataLayer ─── */
declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/* ─── Internal helper ─── */
function pushToDataLayer(data: Record<string, unknown>): void {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(data);
  }
}

/* ─── Generic event push ─── */
export function trackEvent(event: string, params?: Record<string, unknown>): void {
  pushToDataLayer({ event, ...params });
}

/* ─── Virtual pageview (SPA route change) ─── */
export function trackPageView(path: string, title: string): void {
  pushToDataLayer({
    event: 'page_view',
    page_path: path,
    page_title: title,
  });
}

/* ─── Booking form submitted successfully ─── */
export function trackBookingConversion(
  bookingId: string,
  bookingType: string,
  charterType: string,
): void {
  pushToDataLayer({
    event: 'booking_submitted',
    booking_id: bookingId,
    booking_type: bookingType,
    charter_type: charterType,
    currency: 'USD',
  });
}

/* ─── Contact form submitted successfully ─── */
export function trackContactFormSubmission(inquiryType: string): void {
  pushToDataLayer({
    event: 'contact_form_submitted',
    inquiry_type: inquiryType,
  });
}
